import { useCallback, useEffect, useRef, useState } from "react";
import { App } from "@capacitor/app";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { getSellerNativePlatform } from "@/lib/native/runtime";
import {
  checkNativeStoreUpdate,
  completeAndroidFlexibleUpdate,
  isNativeUpdateCheckDue,
  onAndroidFlexibleUpdate,
  openNativeAppStore,
  startAndroidFlexibleUpdate,
  type NativeStoreUpdate,
} from "../../lib/native/store-update";

type BannerPhase = "available" | "downloading" | "ready";

let lastCheckedAt = 0;

export function NativeUpdateBootstrap() {
  const t = useTranslations();
  const [update, setUpdate] = useState<NativeStoreUpdate | null>(null);
  const [phase, setPhase] = useState<BannerPhase>("available");
  const [isActing, setIsActing] = useState(false);
  const updateRef = useRef<NativeStoreUpdate | null>(null);

  useEffect(() => {
    updateRef.current = update;
  }, [update]);

  const check = useCallback(async () => {
    if (!getSellerNativePlatform() || !isNativeUpdateCheckDue(lastCheckedAt)) {
      return;
    }

    lastCheckedAt = Date.now();
    const available = await checkNativeStoreUpdate();
    if (available) {
      setUpdate(available);
      setPhase(available.downloaded ? "ready" : "available");
    }
  }, []);

  useEffect(() => {
    const platform = getSellerNativePlatform();
    if (!platform) {
      return;
    }

    const initialCheckFrame = window.requestAnimationFrame(() => void check());
    const appStateHandle = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void check();
      }
    });
    const stopFlexibleListener =
      platform === "android"
        ? onAndroidFlexibleUpdate((nextPhase) => {
            if (nextPhase === "ready") {
              setPhase("ready");
            } else if (nextPhase === "downloading") {
              setPhase("downloading");
            } else {
              setPhase("available");
            }
          })
        : () => undefined;

    return () => {
      window.cancelAnimationFrame(initialCheckFrame);
      void appStateHandle.then((handle) => handle.remove());
      stopFlexibleListener();
    };
  }, [check]);

  const handleAction = useCallback(async () => {
    const available = updateRef.current;
    if (!available || isActing) {
      return;
    }

    setIsActing(true);
    try {
      if (available.platform === "ios") {
        await openNativeAppStore(available);
        return;
      }

      if (phase === "ready") {
        await completeAndroidFlexibleUpdate();
        return;
      }

      const result = await startAndroidFlexibleUpdate();
      if (result === "started") {
        setPhase("downloading");
      } else if (result === "store") {
        await openNativeAppStore(available);
      }
    } catch {
      // Keep the banner actionable. Store/plugin failures must not block use.
      setPhase("available");
    } finally {
      setIsActing(false);
    }
  }, [isActing, phase]);

  if (!update) {
    return null;
  }

  const message =
    phase === "ready"
      ? t(keys.pwa.nativeUpdateReady)
      : phase === "downloading"
        ? t(keys.pwa.nativeUpdateDownloading, { version: update.version })
        : t(keys.pwa.nativeUpdateAvailable, { version: update.version });
  const action =
    update.platform === "ios"
      ? t(keys.pwa.openStore)
      : phase === "ready"
        ? t(keys.pwa.restartToUpdate)
        : phase === "downloading"
          ? t(keys.pwa.downloading)
          : t(keys.pwa.downloadUpdate);

  return (
    <div
      role="status"
      className="fixed inset-x-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-[60] mx-auto flex max-w-md items-center justify-between gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 shadow-lg"
    >
      <span className="text-sm text-[color:var(--text-primary)]">{message}</span>
      <button
        type="button"
        onClick={() => void handleAction()}
        disabled={isActing || phase === "downloading"}
        className="shrink-0 rounded-lg bg-[color:var(--primary-500)] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[color:var(--primary-600)] disabled:cursor-wait disabled:opacity-60"
      >
        {action}
      </button>
    </div>
  );
}
